import { NameColumn } from '@/components/NameColumn';
import { getColumnWidth } from '@/components/table/columns/registry';
import type { Columns } from '@/components/table/types';
import type { Technology } from '@/types';
import { formatPrettyName } from '@/utils/mappings.util';

/**
 * Builds column definitions for Technology entity tables
 *
 * Returns columns for:
 * - vendor: Technology vendor name with product as description
 * - product: Product name
 * - version: Version string
 * - cpe: Full CPE string (truncated)
 *
 * @returns Column definitions for TanStack Table
 */
export function buildTechnologyColumns(): Columns<Technology> {
  return [
    {
      id: 'vendor',
      label: 'Vendor',
      ...getColumnWidth('technology', 'vendor'),
      cell: (item: Technology) => {
        if (!item?.vendor) {
          return '-';
        }
        // Display vendor with product as description
        const vendorDisplay = formatPrettyName(item.vendor);
        const productDescription = item.product
          ? formatPrettyName(item.product)
          : undefined;
        return (
          <NameColumn name={vendorDisplay} description={productDescription} />
        );
      },
    },
    {
      id: 'product',
      label: 'Product',
      ...getColumnWidth('technology', 'product'),
      cell: (item: Technology) => {
        if (!item?.product) {
          return '-';
        }
        return <span>{formatPrettyName(item.product)}</span>;
      },
    },
    {
      id: 'version',
      label: 'Version',
      ...getColumnWidth('technology', 'version'),
      cell: (item: Technology) => {
        if (!item?.version) {
          return '-';
        }
        return <span>{item.version}</span>;
      },
    },
    {
      id: 'cpe',
      label: 'CPE',
      minWidth: 200,
      sorting: false,
      cell: (item: Technology) => {
        if (!item?.cpe) {
          return '-';
        }
        // Truncate long CPE strings
        const maxLength = 50;
        const displayCpe =
          item.cpe.length > maxLength
            ? `${item.cpe.substring(0, maxLength)}...`
            : item.cpe;
        return <span title={item.cpe}>{displayCpe}</span>;
      },
    },
    {
      id: 'created',
      label: 'First Seen',
      ...getColumnWidth('shared', 'created'),
      cell: 'date' as const,
    },
    {
      id: 'visited',
      label: 'Last Seen',
      ...getColumnWidth('shared', 'visited'),
      cell: 'date' as const,
    },
  ];
}
